import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CalendarClock, CheckCircle2, Users, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/schedule`;

export default function PublicGroupSchedule() {
  const { slug, token } = useParams();
  const [session, setSession] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", mobile: "", city: "", state: "", company_website: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${slug}/group/${token}`).then((r) => setSession(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This session is unavailable."));
  }, [slug, token]);

  const book = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/${slug}/group/${token}/book`, form);
      setDone(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not reserve your seat."); } finally { setBusy(false); }
  };

  const fmt = (iso, tz) => new Date(iso).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz });

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {err && !session ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="group-error">
            <CalendarClock className="w-10 h-10 mx-auto text-slate-300 mb-3" /><p className="text-slate-600">{err}</p>
          </div>
        ) : !session ? <p className="text-center text-slate-400">Loading…</p>
        : done ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="group-confirmed">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
            <h1 className="font-display text-xl font-bold text-slate-900">You're reserved</h1>
            <p className="text-slate-500 mt-2">{session.title}</p>
            <p className="text-slate-700 font-medium mt-1">{fmt(session.start, session.timezone)}</p>
            <p className="text-xs text-slate-400 mt-4">A confirmation has been emailed to you.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="group-page">
            <div className="bg-[#00293f] text-white px-8 py-6">
              {session.recruiter?.logo_url ? <img src={session.recruiter.logo_url} alt="" className="h-8 mb-3" /> : null}
              <h1 className="font-display text-xl font-bold">{session.title}</h1>
              {session.recruiter?.display_name && <p className="text-white/70 text-sm mt-1">Hosted by {session.recruiter.display_name}{session.recruiter.title ? `, ${session.recruiter.title}` : ""}</p>}
            </div>
            <div className="p-8">
              {session.description && <p className="text-sm text-slate-600 mb-4">{session.description}</p>}
              <div className="space-y-2 text-sm mb-5">
                <div className="flex items-center gap-2 text-slate-700"><CalendarClock className="w-4 h-4 text-brand" />{fmt(session.start, session.timezone)}</div>
                {session.location && <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-brand" />{session.location}</div>}
                {"seats_remaining" in session && <div className="flex items-center gap-2 text-slate-700" data-testid="group-seats"><Users className="w-4 h-4 text-brand" />{session.full ? "Session full" : `${session.seats_remaining} seat${session.seats_remaining === 1 ? "" : "s"} remaining`}</div>}
              </div>
              {session.full ? (
                <p className="text-center text-sm text-amber-600 py-4" data-testid="group-full">This session is full. Please contact your recruiter for other options.</p>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Fld label="First Name"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="grp-first" /></Fld>
                    <Fld label="Last Name"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="grp-last" /></Fld>
                  </div>
                  <Fld label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="grp-email" /></Fld>
                  <Fld label="Mobile Phone"><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="grp-mobile" /></Fld>
                  <input type="text" value={form.company_website} onChange={(e) => setForm({ ...form, company_website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" />
                  {err && <p className="text-sm text-rose-600" data-testid="group-book-error">{err}</p>}
                  <Button className="w-full bg-brand hover:bg-brand-strong" onClick={book} disabled={busy || !form.first_name || !form.email} data-testid="grp-reserve">{busy ? "Reserving…" : "Reserve My Seat"}</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function Fld({ label, children }) { return <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>{children}</div>; }
